/**
 * One team, opened: its spaces (open one, make one), its members (owners change roles and
 * remove people; anyone leaves) and, for owners, its invite links.
 */
import { For, Show, createSignal, type JSX } from "solid-js";
import type { Role } from "@protocol/Role";
import { inviteText } from "../../client/space";
import { useApp } from "../../app/context";
import { ROLES, atLeast } from "../../state/access";
import { toast } from "../../shared/toast";
import { ArmedButton, copyText, formatDate } from "../../shared/ui";

/** What each role may do, in a few words. */
const ROLE_HINTS: Record<Role, string> = {
  viewer: "sees the spaces and the others",
  commenter: "sees and comments",
  editor: "changes the spaces, makes new ones",
  owner: "everything, and who is in the team",
};

/** The panel of team `id` on `server`. */
export function TeamPanel(props: { server: string; id: string }): JSX.Element {
  const { accounts, spaces } = useApp();
  const [spaceName, setSpaceName] = createSignal("");
  const [role, setRole] = createSignal<Role>("editor");
  const [once, setOnce] = createSignal(false);
  const team = () => accounts.team(props.server, props.id);
  const me = () => accounts.me(props.server)?.account.id;
  const owner = () => team()?.role === "owner";
  /** The link of invite `code`. */
  const link = (code: string) => inviteText({ server: props.server, code });
  /** Copies an invite link and says so. */
  const copy = async (code: string) => toast((await copyText(link(code))) ? "Invite link copied." : "Copy is blocked here: select the link instead.");

  /** Makes the space named in the field, and opens it. */
  const createSpace = async () => {
    const space = await accounts.createSpace(props.server, props.id, spaceName());
    if (space) {
      setSpaceName("");
      spaces.open({ server: props.server, space: space.id });
    }
  };

  return (
    <div class="home-teampanel" data-team-panel={props.id}>
      <Show when={team()} fallback={<p class="small muted">Loading…</p>}>
        {(view) => (
          <>
            <h4>Spaces</h4>
            <ul class="share-list">
              <For each={view().spaces} fallback={<li class="muted small">No space yet.</li>}>
                {(space) => (
                  <li data-team-space={space.id} data-current={spaces.current?.space === space.id && spaces.current?.server === props.server ? "yes" : "no"}>
                    <b>{space.name}</b>
                    <span class="muted small">{formatDate(space.created)}</span>
                    <span class="btnrow">
                      <button type="button" class="mini" data-open-space onClick={() => spaces.open({ server: props.server, space: space.id })}>
                        Open
                      </button>
                    </span>
                  </li>
                )}
              </For>
            </ul>
            <Show when={atLeast(view().role, "editor")}>
              <form
                class="row-inline"
                onSubmit={(e) => {
                  e.preventDefault();
                  void createSpace();
                }}
              >
                <input class="field" data-new-space-name placeholder="New space name" value={spaceName()} onInput={(e) => setSpaceName(e.currentTarget.value)} />
                <button type="submit" class="btn primary" data-new-space>
                  New space
                </button>
              </form>
            </Show>

            <h4>Members ({view().members.length})</h4>
            <ul class="share-list">
              <For each={view().members}>
                {(member) => (
                  <li data-team-member={member.id} data-member-role={member.role}>
                    <b>{member.name}</b>
                    <Show when={member.id === me()}>
                      <span class="muted small"> (you)</span>
                    </Show>
                    <span class="mono small">{member.email}</span>
                    <Show when={owner()} fallback={<span>{member.role}</span>}>
                      <select class="field" data-member-select value={member.role} onChange={(e) => void accounts.setRole(props.server, props.id, member.id, e.currentTarget.value as Role)}>
                        <For each={ROLES}>{(r) => <option value={r}>{r}</option>}</For>
                      </select>
                    </Show>
                    <Show when={owner() || member.id === me()}>
                      <ArmedButton
                        class="mini"
                        label={member.id === me() ? "Leave" : "Remove"}
                        armedLabel={member.id === me() ? "Click again to leave" : "Click again to remove"}
                        onConfirm={() => void accounts.removeMember(props.server, props.id, member.id)}
                      />
                    </Show>
                  </li>
                )}
              </For>
            </ul>

            <Show when={owner()}>
              <h4>Invite</h4>
              <div class="row-inline">
                <select class="field" data-invite-role value={role()} onChange={(e) => setRole(e.currentTarget.value as Role)}>
                  <For each={ROLES}>{(r) => <option value={r}>{r}</option>}</For>
                </select>
                <label class="small">
                  <input type="checkbox" data-invite-once checked={once()} onChange={(e) => setOnce(e.currentTarget.checked)} /> once only
                </label>
                <button
                  type="button"
                  class="btn primary"
                  data-invite-create
                  onClick={async () => {
                    const invite = await accounts.invite(props.server, props.id, role(), once());
                    if (invite) void copy(invite.code);
                  }}
                >
                  Create link
                </button>
              </div>
              <p class="small muted">{ROLE_HINTS[role()]}. Whoever opens the link signs in (or makes an account) and joins with this role.</p>
              <ul class="share-list">
                <For each={view().invites} fallback={<li class="muted small">No invite link yet.</li>}>
                  {(invite) => (
                    <li data-invite={invite.code} data-invite-role={invite.role}>
                      <b>{invite.role}</b>
                      <span class="mono small" data-invite-link>
                        {link(invite.code)}
                      </span>
                      <span class="muted small">
                        {invite.once ? "once" : "reusable"} · used {invite.used} · {formatDate(invite.created)}
                      </span>
                      <span class="btnrow">
                        <button type="button" class="mini" data-invite-copy onClick={() => void copy(invite.code)}>
                          Copy
                        </button>
                        <button type="button" class="mini" data-invite-revoke onClick={() => void accounts.revoke(props.server, props.id, invite.code)}>
                          Withdraw
                        </button>
                      </span>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </>
        )}
      </Show>
    </div>
  );
}
